import React from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { When } from 'react-if'
import { LinkButton } from '@/atoms/Button'
import { CaptionSM, TitleXS } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { useTranslate } from '@/utils/translate/translate-client'
import { useGuildSignupContext } from '../GuildSignupContext'
import { GuildEmailInput } from './EmailInput'

interface StickyCallToActionProps {
  hasUser: boolean
  isVisible: boolean
}

export const StickyCallToAction: React.FC<StickyCallToActionProps> = ({ hasUser, isVisible }) => {
  const { t } = useTranslate('guild')
  const { experiments } = useGuildSignupContext()

  const showEmailInput = Boolean(experiments?.['sticky-cta-guild-signup-pages'] === 'variation_2')

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed inset-x-0 bottom-0 z-[100] max-w-full pt-[1px]"
          style={{
            backgroundImage:
              'linear-gradient(166.78deg, #EDD7C6 1.42%, #D6A177 14.6%, #FEDEC6 39.98%, #D48D59 60.48%, #A66141 85.7%, #612B12 94.6%)',
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        >
          <div className="z-[100] flex h-full w-full flex-row items-center bg-core-gray-950 p-4">
            <When condition={!showEmailInput}>
              <div className="hidden md:flex md:w-full md:flex-col md:gap-2">
                <TitleXS weight="bold" color="white" className="lg:photon-title-sm">
                  {t('joinMovementChangingEntertainment', 'Join the Movement Changing Entertainment')}
                </TitleXS>
                <CaptionSM color="core-gray-400" className="lg:photon-caption-md">
                  {t(
                    'supportFilmmakersCancelAnytime',
                    'Support films & filmmakers that share your values. Cancel anytime.',
                  )}
                </CaptionSM>
              </div>
              <LinkButton
                variant="white"
                href={paths.guild.signup.createAccount}
                className="max-h-8 w-full !border-[#FDA21E] !bg-[#FDA21E] px-4 py-2 md:w-fit md:min-w-[129px] lg:text-sm"
              >
                {t('getStarted', 'Get Started')}
              </LinkButton>
            </When>
            <When condition={showEmailInput}>
              <div className="hidden md:flex md:w-full md:flex-col md:gap-2">
                <TitleXS weight="bold" color="white" className="lg:photon-title-sm">
                  {t('joinMovementChangingEntertainment', 'Join the Movement Changing Entertainment')}
                </TitleXS>
                <CaptionSM color="core-gray-400" className="lg:photon-caption-md">
                  {t(
                    'supportFilmmakersCancelAnytime',
                    'Support films & filmmakers that share your values. Cancel anytime.',
                  )}
                </CaptionSM>
              </div>
              <div>
                <GuildEmailInput hasUser={hasUser} className="md:max-w-[350px] lg:max-w-[471px]" />
              </div>
            </When>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
